import { Fragment, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../../lib/cx';

/** Gap between the anchor and the bubble, and the minimum clearance from a viewport edge. */
const GAP = 6;
const EDGE = 8;
/**
 * Match the `w-72`/`w-[26rem]` classes below — used only as a fallback when the bubble has
 * no measured width yet. The list variant is wider so a definition does not wrap after two
 * words; both are still clamped to the viewport at render time.
 */
const WIDTH_PROSE = 288;
const WIDTH_LIST = 416;

interface Position {
  top: number;
  left: number;
}

/** One `term — description` pair of a legend-style tip, e.g. a status and what it means. */
export interface HelpTipItem {
  term: string;
  description: string;
}

interface HelpTipBaseProps {
  /**
   * Accessible name of the button itself, e.g. "What does FILES WAITING show?" — the `?`
   * glyph carries no meaning, so the name has to say which thing is being explained.
   */
  label: string;
  /**
   * The thing being explained — a tile, a row, a field. While the tip is open this element
   * carries `aria-describedby`, so a screen reader reads the explanation as part of it
   * rather than as a stray fragment, and the bubble is placed clear of this element's box
   * so it can never cover the value it explains. Defaults to the button.
   */
  anchor?: RefObject<HTMLElement | null>;
  className?: string;
}

/**
 * The two content shapes are mutually exclusive by construction: `text` for a one- or
 * two-sentence explanation (the summary tiles), `items` for a legend of several terms
 * (the STATUS column). `?: never` on the other member makes passing both a compile error
 * rather than a silent precedence rule nobody can see from the call site.
 */
export type HelpTipProps =
  | (HelpTipBaseProps & {
      /** The explanation. Plain text: it is rendered as text content, never as HTML. */
      text: string;
      items?: never;
    })
  | (HelpTipBaseProps & {
      /**
       * A legend: rendered as a real `<dl>`, one `<dt>`/`<dd>` pair per entry, so a screen
       * reader announces N terms with N definitions instead of one run-on sentence.
       */
      items: readonly HelpTipItem[];
      text?: never;
    });

/**
 * A muted `?` that reveals a one- or two-sentence explanation of the value next to it
 * (see the "Summary strip" section). It is a real button: reachable by keyboard, shown on hover *and* on
 * focus, dismissed by `Escape`, blur, or the pointer leaving.
 */
export function HelpTip(props: HelpTipProps) {
  const { label, anchor, className } = props;
  const items = 'items' in props ? props.items : undefined;
  const tipId = useId();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);

  /**
   * Fixed-position placement measured from the anchor, so the bubble is bound by the
   * viewport rather than by the anchor's own (possibly clipped) box. It sits *below* the
   * anchor by default — which both keeps it off the number above it and makes it
   * impossible to clip against the top of the viewport for a first row of tiles — and
   * only flips above when there is no room below and there is room above.
   */
  const place = useCallback(() => {
    const anchorEl = anchor?.current ?? buttonRef.current;
    const tip = tipRef.current;
    if (!anchorEl || !tip) return;

    const from = anchorEl.getBoundingClientRect();
    const bubble = tip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const fallback = items ? WIDTH_LIST : WIDTH_PROSE;
    const width = bubble.width || Math.min(fallback, viewportWidth - 2 * EDGE);

    // Right-aligned with the anchor (the icon lives in its top-right corner), then
    // clamped so neither edge of the viewport can cut it off at narrow widths.
    const left = Math.max(EDGE, Math.min(from.right - width, viewportWidth - width - EDGE));

    let top = from.bottom + GAP;
    const above = from.top - bubble.height - GAP;
    if (bubble.height && top + bubble.height > viewportHeight - EDGE && above >= EDGE) {
      top = above;
    }

    setPosition((previous) =>
      previous.top === top && previous.left === left ? previous : { top, left },
    );
  }, [anchor, items]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Swallowed here so dismissing a tip inside a modal does not also close the modal.
      event.stopPropagation();
      setOpen(false);
    };
    const reposition = () => place();

    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', reposition);
    // Capture phase: scrolling happens on an inner container, not always on the window.
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, place]);

  // `aria-describedby` is put on the anchor rather than on the button, so the explanation
  // is read as part of the tile. The anchor is not rendered here, hence the attribute is
  // set directly on it, and whatever it described before is restored on the way out.
  useEffect(() => {
    const element = anchor?.current;
    if (!open || !element) return undefined;

    const previous = element.getAttribute('aria-describedby');
    element.setAttribute('aria-describedby', previous ? `${previous} ${tipId}` : tipId);
    return () => {
      if (previous) element.setAttribute('aria-describedby', previous);
      else element.removeAttribute('aria-describedby');
    };
  }, [anchor, open, tipId]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-describedby={open && !anchor ? tipId : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className={cx(
          'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border',
          'border-hairline-strong bg-surface text-[10px] font-semibold leading-none',
          'text-ink-subtle transition-colors hover:border-ink-subtle hover:text-ink-muted',
          className,
        )}
      >
        ?
      </button>
      {open
        ? createPortal(
            <div
              ref={tipRef}
              id={tipId}
              role="tooltip"
              style={{
                top: position.top,
                left: position.left,
                maxWidth: `calc(100vw - ${2 * EDGE}px)`,
              }}
              className={cx(
                'fixed z-40 rounded border border-hairline-strong bg-surface-raised',
                'px-2.5 py-2 text-2xs leading-relaxed text-ink shadow-popover',
                items ? 'w-[26rem]' : 'w-72',
              )}
            >
              {items ? (
                // A grid rather than line breaks: every term starts in the same column, so
                // the eye finds the boundaries between entries without reading them.
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  {items.map((item) => (
                    <Fragment key={item.term}>
                      <dt className="font-mono text-ink">{item.term}</dt>
                      <dd className="text-ink-muted">{item.description}</dd>
                    </Fragment>
                  ))}
                </dl>
              ) : (
                props.text
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
